import { z } from "zod";
import { isoDateSchema, localTimeSchema, uuidSchema } from "./common.ts";
import { timetableGroupSchema } from "./club.ts";

/**
 * The owner timetable (prototype `teamna/prototypes/owner-timetable.html`, approved 2026-10-09).
 * The owner edits the whole week as a draft in the browser and publishes it in one step: every
 * weekly time, the end of the term, and the closed dates. A dry run of the same call says what the
 * publish would do to booked people, so the review never guesses.
 */

/** A closed date. `templateId` null: the whole club. Set: only that weekly time on that date. */
export const timetableClosureSchema = z.object({
  date: isoDateSchema,
  templateId: uuidSchema.nullable(),
  reason: z.string().nullable(),
});
export type TimetableClosure = z.infer<typeof timetableClosureSchema>;

/** A future session of a weekly time, with the people booked on it. */
export const timetableEditorSessionSchema = z.object({
  id: uuidSchema,
  templateId: uuidSchema,
  /** The session's day in the business timezone. */
  date: isoDateSchema,
  bookedCount: z.number().int(),
});
export type TimetableEditorSession = z.infer<typeof timetableEditorSessionSchema>;

/** A month, `yyyy-MM`. */
export const monthKeySchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Expected yyyy-MM");

/**
 * A month of the timetable (spec `teamna/strategy/timetable-months.md`). `published`: its first day
 * is on or before the last planned day, so it has its weekly times (or is closed on purpose). `none`:
 * nothing is planned for it yet.
 */
export const timetableMonthStateSchema = z.enum(["published", "none"]);
export type TimetableMonthState = z.infer<typeof timetableMonthStateSchema>;

export const timetableMonthSchema = z.object({
  month: monthKeySchema,
  state: timetableMonthStateSchema,
  /** The owner can open it: this month always, the next month when this month is published. */
  canOpen: z.boolean(),
});
export type TimetableMonth = z.infer<typeof timetableMonthSchema>;

/**
 * `GET /business/businesses/:id/timetable/editor?month=yyyy-MM`: everything the week editor starts
 * from, for one month (no `month`: this month). The month fields are optional for older servers.
 */
export const timetableEditorSchema = z.object({
  /** Today in the business timezone. */
  today: isoDateSchema,
  timezone: z.string(),
  /** How far ahead the nightly job makes sessions. Bookings exist only inside it. */
  horizonDays: z.number().int(),
  /** The end of the term: the last date of every weekly time. Null: no end. */
  until: isoDateSchema.nullable(),
  daysOff: z.array(z.number().int().min(0).max(6)),
  groups: z.array(timetableGroupSchema),
  closures: z.array(timetableClosureSchema),
  sessions: z.array(timetableEditorSessionSchema),
  /** `photoUrl`: the signed profile photo, null for initials. Optional for older servers. */
  team: z.array(z.object({ userId: uuidSchema, name: z.string(), photoUrl: z.string().nullable().optional() })),
  /** The month on screen. `groups[].slots`, `closures` and `sessions` cover this month only. */
  month: monthKeySchema.optional(),
  monthState: timetableMonthStateSchema.optional(),
  /** This month and the next, for the month switch. */
  months: z.array(timetableMonthSchema).optional(),
  /** The month before, as levels with their weekly times: the dashed start of an empty month. */
  previous: z.object({ month: monthKeySchema, groups: z.array(timetableGroupSchema) }).nullable().optional(),
  /** The last date with a weekly session, on or before the last planned day. Null: none. */
  lastSession: isoDateSchema.nullable().optional(),
});
export type TimetableEditor = z.infer<typeof timetableEditorSchema>;

/** A blank session name or line is no name: the session shows its class name (internal review F5.6). */
function emptyToNull(value: string): string | null {
  return value === "" ? null : value;
}

export const publishTimetableSlotSchema = z.object({
  /** The weekly time this row keeps. Null or absent: a new weekly time. */
  id: uuidSchema.nullable().optional(),
  groupId: uuidSchema,
  weekday: z.number().int().min(0).max(6),
  startTime: localTimeSchema,
  durationMin: z.number().int().min(5).max(600),
  /** Null: no limit. */
  capacity: z.number().int().min(1).max(500).nullable(),
  staffUserId: uuidSchema.nullable(),
  /** The session's own name ("Core awake") and what it is ("Mixed apparatus"). Null or absent: the class name. */
  name: z.string().trim().max(60).transform(emptyToNull).nullable().optional(),
  detail: z.string().trim().max(80).transform(emptyToNull).nullable().optional(),
});
export type PublishTimetableSlot = z.infer<typeof publishTimetableSlotSchema>;

export const publishTimetableClosureSchema = z.object({
  date: isoDateSchema,
  /** Null: the whole day. A number: the row at that index in `slots`. */
  slot: z.number().int().min(0).nullable(),
  reason: z.string().max(120).nullable().optional(),
});
export type PublishTimetableClosure = z.infer<typeof publishTimetableClosureSchema>;

/**
 * The body of `POST /business/businesses/:id/timetable/publish` (Idempotency-Key required) and of
 * `POST .../timetable/preview` (the same work, rolled back). The whole timetable, replaced as a set:
 * a row with the id of a weekly time keeps it (and its bookings), a row without an id is new, a
 * weekly time that is not in the list stops. Future closures are replaced as a set too.
 *
 * Strict: a `dryRun` field sent to publish is refused, never published.
 */
export const timetableDraftBodySchema = z
  .object({
    until: isoDateSchema.nullable(),
    slots: z.array(publishTimetableSlotSchema).max(500),
    closures: z.array(publishTimetableClosureSchema).max(500),
  })
  .strict();
export type TimetableDraftBody = z.input<typeof timetableDraftBodySchema>;

/** The publish service's input: the body, and whether to roll it back (the preview route). */
export const publishTimetableRequestSchema = timetableDraftBodySchema.extend({
  dryRun: z.boolean().default(false),
});
export type PublishTimetableRequest = z.input<typeof publishTimetableRequestSchema>;

/** A weekly time of one month. `copiedFrom`: the row of the month before it was copied from. */
export const publishMonthSlotSchema = publishTimetableSlotSchema.extend({
  copiedFrom: uuidSchema.nullable().optional(),
});
export type PublishMonthSlot = z.infer<typeof publishMonthSlotSchema>;

/**
 * The body of `POST /business/businesses/:id/timetable/months/:month/publish` (Idempotency-Key
 * required) and of `.../preview` (the same work, rolled back). One month's weekly times and closed
 * dates, replaced as a set inside the month (from today in this month). The month always runs to its
 * last day. `source`: how the month started, for analytics and the audit.
 */
export const timetableMonthBodySchema = z
  .object({
    slots: z.array(publishMonthSlotSchema).max(500),
    closures: z.array(publishTimetableClosureSchema).max(500),
    source: z.enum(["editor", "month_copy"]).default("editor"),
  })
  .strict();
export type TimetableMonthBody = z.input<typeof timetableMonthBodySchema>;

export const publishMonthRequestSchema = timetableMonthBodySchema.extend({
  dryRun: z.boolean().default(false),
});
export type PublishMonthRequest = z.input<typeof publishMonthRequestSchema>;

export const timetableProblemCodes = [
  /** One person runs two things at the same time. */
  "COACH_CLASH",
  /** A limit under the number already booked on a session of that weekly time. */
  "PLACES_BELOW_BOOKED",
  /** Private hours with nobody to run them. */
  "PRIVATE_NEEDS_COACH",
  /** A weekly time on a day the business is closed every week. */
  "DAY_OFF",
] as const;
export const timetableProblemCodeSchema = z.enum(timetableProblemCodes);
export type TimetableProblemCode = z.infer<typeof timetableProblemCodeSchema>;

export const timetableProblemSchema = z.object({
  code: timetableProblemCodeSchema,
  /** Indexes into the request's `slots`. */
  slots: z.array(z.number().int()),
  detail: z.string(),
});
export type TimetableProblem = z.infer<typeof timetableProblemSchema>;

export const publishTimetableSummarySchema = z.object({
  slotsAdded: z.number().int(),
  slotsChanged: z.number().int(),
  slotsRemoved: z.number().int(),
  /** Sessions that move to a new time, with their bookings. */
  sessionsMoved: z.number().int(),
  /** Sessions that come off: a weekly time stops, the term ends earlier, a closed date. */
  sessionsCancelled: z.number().int(),
  bookingsMoved: z.number().int(),
  /** Bookings that come off. Each one that was paid with a session gets it back. */
  bookingsReturned: z.number().int(),
  /** Accounts that get one message about the publish. */
  parentsTold: z.number().int(),
});
export type PublishTimetableSummary = z.infer<typeof publishTimetableSummarySchema>;

export const publishTimetableResultSchema = z.object({
  published: z.boolean(),
  problems: z.array(timetableProblemSchema),
  summary: publishTimetableSummarySchema,
});
export type PublishTimetableResult = z.infer<typeof publishTimetableResultSchema>;
